import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Download, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

function recentMonths(count = 6) {
  const now = new Date();
  const list = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    list.push(ym);
  }
  return list;
}

export default function CommissionStatementCard({ courierId }) {
  const [month, setMonth] = useState(recentMonths(1)[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!courierId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/commissions/history/${courierId}?month=${month}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [courierId, month]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/commissions/pdf/${courierId}?month=${month}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ARTEKNOS_releve_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF download failed", e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="bg-[#0F1B33] border border-[#D4AF37]/30" data-testid="commission-statement-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-black uppercase tracking-widest text-white">
          <FileText className="w-4 h-4 text-[#D4AF37]" />
          Mon relevé commissions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-[#020617] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40"
            data-testid="commission-month-select"
          >
            {recentMonths(6).map((ym) => (
              <option key={ym} value={ym}>{monthLabel(ym)}</option>
            ))}
          </select>
          <Button
            onClick={handleDownload}
            disabled={downloading || !data || data.orders_count === 0}
            className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#0F172A] rounded-full px-4 py-2 text-xs font-black uppercase tracking-widest ml-auto"
            data-testid="download-commission-pdf-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            PDF
          </Button>
        </div>

        {loading ? (
          <p className="text-xs text-slate-400 text-center py-4">Chargement…</p>
        ) : !data || data.orders_count === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">
            Aucune course sur {monthLabel(month)}.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-[#020617] rounded-xl p-3 border border-white/5">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Courses</p>
              <p className="text-xl font-black text-white">{data.orders_count}</p>
            </div>
            <div className="bg-[#020617] rounded-xl p-3 border border-white/5">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Distance</p>
              <p className="text-xl font-black text-white">{data.total_km} km</p>
            </div>
            <div className="bg-[#020617] rounded-xl p-3 border border-white/5">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Revenu brut</p>
              <p className="text-xl font-black text-emerald-400">{fmt(data.total_revenue)} F</p>
            </div>
            <div className="bg-[#020617] rounded-xl p-3 border border-[#D4AF37]/30">
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Commission 10%</p>
              <p className="text-xl font-black text-[#D4AF37]">-{fmt(data.total_commission)} F</p>
            </div>
            <div className="col-span-2 bg-gradient-to-r from-emerald-500/15 to-emerald-700/10 rounded-xl p-3 border border-emerald-500/30">
              <p className="text-[10px] uppercase tracking-widest text-emerald-300">Net chauffeur</p>
              <p className="text-2xl font-black text-white">{fmt(data.total_net)} FCFA</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
